import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { LoaderService } from '@services/support/loader.service';
import { AccountService } from '@services/account.service';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { ModalService } from '@services/remote/modal.service';
import { AuthService } from '@services/auth.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';


@Component({
    selector: 'app-storage',
    templateUrl: './storage.component.html',
    styleUrls: ['./storage.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StorageComponent implements OnInit, OnDestroy {

  @ViewChild("checkAzureTemplate", { static: true }) private checkAzureTemplate: TemplateRef<any>;
  @ViewChild("connectAzureTemplate", { static: true }) private connectAzureTemplate: TemplateRef<any>;
  @ViewChild("disconnectAzureTemplate", { static: true }) private disconnectAzureTemplate: TemplateRef<any>;

  azureInfo: any = null;
  allowArchiving: boolean = false;
  allowAzureIntegration: boolean = false;
  accountDataSub: Subscription = null;

  azureCredentials: any = {
    accountName: "",
    containerName: "",
    accountKey: ""
  }

  sharedAzureProgress = [];

  webdavEnabled: boolean = false;

  constructor(
    private accountService: AccountService,
    private loaderService: LoaderService,
    private modalService: ModalService,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {

    this.authService.getAccountData().then(account => {
      this.azureInfo = account.data.accountData.third_parties.azure;
      this.allowArchiving = account.data.accountData.features.archiving;
      this.allowAzureIntegration = account.data.accountData.features.azureintegration;
      this.webdavEnabled = account.data.accountData.add_ons.webdav;

      if ((this.azureInfo.shared_storage.expire > 0) || (this.azureInfo.shared_storage.expire === 0 && this.azureInfo.shared_storage.dedicated === 0)) {
        this.sharedAzureProgress = [
          {
            value: 100,
            type: 'primary',
            label: 'UNLIMITED'
          }
        ];
      } else if (this.azureInfo.shared_storage.dedicated > 0) {
        let percent = (this.azureInfo.shared_storage.size / (this.azureInfo.shared_storage.dedicated * 1073741824));
        percent = (percent > 1) ? 1 : percent;
        this.sharedAzureProgress = [
          {
            value: (percent * 100),
            type: percent < 0.9 ? 'info' : 'danger',
            label: (percent * 100).toFixed(0) +'%'
          }
        ];
      }
    })

  }

  ngOnDestroy() {
    if (this.accountDataSub) { this.accountDataSub.unsubscribe() }
  }

  onCheckAzure(connect: boolean) {
    if (this.allowAzureIntegration) {
      this.loaderService.show();
      this.accountService.checkArchiveStatuses()
      .then(result => {
        if (result.pending) {
          const modalId = this.modalService.show({
            template: this.checkAzureTemplate,
            context: {
              dataModel: { connect: connect },
              callbacks: {
                cancel: () => {
                  this.modalService.hide(modalId);
                },
                continue: () => {
                  this.modalService.hide(modalId);
                  if (connect) {
                    this.onConnectAzure();
                  } else {
                    this.onDisconnectAzure();
                  }
                }
              }
            }
          });
        } else {
          if (connect) {
            this.onConnectAzure();
          } else {
            this.onDisconnectAzure();
          }
        }
      })
      .catch(error => {
        console.log(error);
        this.snackBarService.error(this.translateService.instant('MAIN.STORAGE.MESSAGES.CANNOT_CONNECT'));
      })
      .finally(() => {
        this.loaderService.hide();
      });
    }
  }

  onConnectAzure() {
    const modalId = this.modalService.show({
      template: this.connectAzureTemplate,
      context: {
        dataModel: this.azureCredentials,
        callbacks: {
          cancel: () => {
            this.azureCredentials = {
              accountName: "",
              containerName: "",
              accountKey: ""
            }
            this.modalService.hide(modalId);
          },
          connect: (azureForm: NgForm) => {
            this.loaderService.show();
            azureForm.control.disable();

            this.accountService.connectAzureAccount(azureForm.value.accountName, azureForm.value.containerName, azureForm.value.accountKey)
            .then(() => {
              this.modalService.hide(modalId);
              this.snackBarService.success(this.translateService.instant('MAIN.STORAGE.MESSAGES.AZURE_CONNECTED'));
            })
            .catch(error => {
              azureForm.control.enable();
              this.snackBarService.error(this.translateService.instant('MAIN.STORAGE.MESSAGES.CANNOT_CONNECT'));
            })
            .finally(() => {
              this.azureCredentials = {
                accountName: "",
                containerName: "",
                accountKey: ""
              }
              this.loaderService.hide();
            });
          }
        }
      }
    });
  }

  onDisconnectAzure() {
    const modalId = this.modalService.show({
      template: this.disconnectAzureTemplate,
      context: {
        dataModel: null,
        callbacks: {
          cancel: () => {
            this.modalService.hide(modalId);
          },
          disconnect: () => {
            this.loaderService.show();

            this.accountService.disconnectAzureAccount()
            .then(() => this.snackBarService.success(this.translateService.instant('MAIN.STORAGE.MESSAGES.AZURE_DISCONNECTED')))
            .catch(error => this.snackBarService.error(this.translateService.instant('MAIN.STORAGE.MESSAGES.CANNOT_DISCONNECT')))
            .finally(() => {
              this.modalService.hide(modalId);
              this.loaderService.hide();
            });
          }
        }
      }
    });
  }
}
